/**
 * PEC Quick Actions — Iter-110b.5 (Field Reality Layer)
 *
 * Floating Action Button (FAB) + 4 bottom sheets optimized for site engineers:
 *   • Quick Daily Log  — "Same as yesterday" + ± labor buttons + area chips
 *   • Quick MAR        — status chips + multi-file upload + auto-fill area
 *   • Quick MIR        — status chips + before/during/after multi-photo
 *   • Quick Photo      — instant camera capture with GPS attached
 *
 * Goal: any PEC record entered in <60 seconds with ≤5 taps on a phone.
 * GPS is requested once on mount and cached for 10 minutes.
 */
import React, { useEffect, useRef, useState, useCallback } from "react";
import { axiosInstance } from "../../App";
import { toast } from "sonner";
import {
  Plus, X, Camera, Calendar, Boxes, ClipboardCheck, Upload,
  Minus, MapPin, History, Image as ImageIcon, ChevronUp,
} from "lucide-react";

const MAR_STATUSES = ["Draft", "Submitted", "Under Review", "Approved", "Rejected"];
const MIR_STATUSES = ["Pending", "Inspected", "Approved", "Rework", "Closed"];

const GPS_CACHE_KEY = "pec:gps";
const GPS_TTL_MS = 10 * 60 * 1000;          // 10 minutes
const STORAGE_AREAS = (pid) => `pec:recentAreas:${pid}`;
const STORAGE_ENGINEER = (pid) => `pec:lastEngineer:${pid}`;

// ─── GPS helper (one prompt, 10-min cache) ──────────────────────
function useGps() {
  const [coords, setCoords] = useState(() => {
    try {
      const cached = JSON.parse(localStorage.getItem(GPS_CACHE_KEY) || "null");
      if (cached && Date.now() - cached.ts < GPS_TTL_MS) {
        return { lat: cached.lat, lng: cached.lng };
      }
    } catch { /* ignore */ }
    return null;
  });
  useEffect(() => {
    if (coords) return;       // already from cache
    if (!navigator.geolocation) return;
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        const c = { lat: pos.coords.latitude, lng: pos.coords.longitude };
        setCoords(c);
        try { localStorage.setItem(GPS_CACHE_KEY, JSON.stringify({ ...c, ts: Date.now() })); } catch { /* ignore */ }
      },
      () => { /* user denied — silent fallback */ },
      { timeout: 8000, maximumAge: 5 * 60 * 1000 },
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  return coords;
}

// ─── Helpers ────────────────────────────────────────────────────
async function quickUpload(projectId, kind, file, gps) {
  const fd = new FormData();
  fd.append("project_id", projectId);
  fd.append("kind", kind);
  fd.append("file", file);
  if (gps?.lat != null) fd.append("lat", String(gps.lat));
  if (gps?.lng != null) fd.append("lng", String(gps.lng));
  const r = await axiosInstance.post("/pec/upload", fd, {
    headers: { "Content-Type": "multipart/form-data" },
  });
  return r.data;
}

function rememberArea(projectId, areaId) {
  if (!areaId) return;
  try {
    const key = STORAGE_AREAS(projectId);
    const prev = JSON.parse(localStorage.getItem(key) || "[]");
    const next = [areaId, ...prev.filter(a => a !== areaId)].slice(0, 5);
    localStorage.setItem(key, JSON.stringify(next));
  } catch { /* ignore */ }
}

function getRecentAreas(projectId) {
  try { return JSON.parse(localStorage.getItem(STORAGE_AREAS(projectId)) || "[]"); }
  catch { return []; }
}

// ─── Bottom Sheet shell ─────────────────────────────────────────
function Sheet({ open, onClose, title, children }) {
  if (!open) return null;
  return (
    <div
      data-testid="qa-sheet"
      className="fixed inset-0 z-[60] flex items-end justify-center bg-slate-950/60 backdrop-blur-sm"
      onClick={onClose}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-2xl rounded-t-3xl bg-white p-5 shadow-2xl max-h-[88vh] overflow-y-auto"
        style={{ animation: "slideup 220ms ease-out" }}
      >
        <div className="mb-4 flex items-center justify-between">
          <h3 className="text-base font-bold text-slate-900">{title}</h3>
          <button data-testid="qa-sheet-close" onClick={onClose} className="rounded-full p-2 text-slate-500 hover:bg-slate-100">
            <X className="h-5 w-5" />
          </button>
        </div>
        {children}
      </div>
      <style>{`@keyframes slideup { from { transform: translateY(100%); } to { transform: translateY(0); } }`}</style>
    </div>
  );
}

// ─── Counter +/- pill (touch-friendly) ──────────────────────────
function CounterPill({ value, onChange, label, testid }) {
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-3">
      <div className="mb-1 text-[11px] font-semibold text-slate-600">{label}</div>
      <div className="flex items-center gap-2">
        <button
          data-testid={`${testid}-minus`}
          onClick={() => onChange(Math.max(0, Number(value || 0) - 1))}
          className="inline-flex h-11 w-11 items-center justify-center rounded-full bg-slate-100 text-slate-700 hover:bg-slate-200 active:scale-95"
        >
          <Minus className="h-4 w-4" />
        </button>
        <input
          data-testid={testid}
          type="number"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className="w-full rounded-lg border border-slate-200 px-2 py-2 text-center text-xl font-bold text-slate-900"
        />
        <button
          data-testid={`${testid}-plus`}
          onClick={() => onChange(Number(value || 0) + 1)}
          className="inline-flex h-11 w-11 items-center justify-center rounded-full bg-violet-100 text-violet-700 hover:bg-violet-200 active:scale-95"
        >
          <Plus className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
}

// ─── Status Chips (touch-friendly, no dropdown) ─────────────────
function StatusChips({ statuses, value, onChange, testid }) {
  return (
    <div data-testid={testid} className="flex flex-wrap gap-2">
      {statuses.map(s => {
        const active = value === s;
        return (
          <button
            key={s}
            data-testid={`${testid}-${s.replace(/\s+/g, "_")}`}
            onClick={() => onChange(s)}
            className={`inline-flex min-h-[40px] items-center rounded-full px-4 py-1.5 text-sm font-semibold transition-all active:scale-95 ${
              active
                ? "bg-violet-600 text-white shadow-md ring-2 ring-violet-300"
                : "bg-slate-100 text-slate-700 hover:bg-slate-200"
            }`}
          >
            {s}
          </button>
        );
      })}
    </div>
  );
}

// ─── Area Chips (last 5 used first) ─────────────────────────────
function AreaChips({ areas, projectId, value, onChange }) {
  const recentIds = getRecentAreas(projectId);
  const ordered = [
    ...recentIds.map(id => areas.find(a => a.id === id)).filter(Boolean),
    ...areas.filter(a => !recentIds.includes(a.id)),
  ];
  return (
    <div data-testid="qa-area-chips" className="flex flex-wrap gap-2">
      <button
        data-testid="qa-area-chip-none"
        onClick={() => onChange("")}
        className={`min-h-[40px] rounded-full px-3 py-1.5 text-sm transition-all active:scale-95 ${
          !value ? "bg-violet-600 text-white" : "bg-slate-100 text-slate-700"
        }`}
      >
        كل المشروع
      </button>
      {ordered.slice(0, 8).map(a => {
        const active = value === a.id;
        const isRecent = recentIds.includes(a.id);
        return (
          <button
            key={a.id}
            data-testid={`qa-area-chip-${a.id}`}
            onClick={() => onChange(a.id)}
            className={`min-h-[40px] rounded-full px-3 py-1.5 text-sm transition-all active:scale-95 ${
              active ? "bg-violet-600 text-white" : "bg-slate-100 text-slate-700 hover:bg-slate-200"
            }`}
          >
            {isRecent && <History className="inline h-3 w-3 ml-1 opacity-60" />}
            {a.name}
          </button>
        );
      })}
    </div>
  );
}

// ─── Multi-camera (keeps button mounted after each upload) ──────
function MultiCamera({ urls, onAdd, gps, projectId, kind, testid, label = "صور" }) {
  const [uploading, setUploading] = useState(false);
  const onChange = async (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;
    setUploading(true);
    const added = [];
    for (const f of files) {
      try {
        const r = await quickUpload(projectId, kind, f, gps);
        added.push(r.url);
      } catch { toast.error(`فشل رفع ${f.name}`); }
    }
    setUploading(false);
    if (added.length) {
      onAdd(added);
      toast.success(`تم رفع ${added.length} صورة`);
    }
    e.target.value = "";
  };
  return (
    <div>
      <div className="mb-1 text-[11px] font-semibold text-slate-600">{label} ({urls.length}{gps ? " · GPS✓" : ""})</div>
      <div className="flex flex-wrap gap-2">
        {urls.map((u, i) => (
          <a key={i} href={u} target="_blank" rel="noreferrer" className="inline-flex h-14 w-14 items-center justify-center rounded-lg border border-slate-200 bg-slate-50">
            <ImageIcon className="h-5 w-5 text-slate-400" />
          </a>
        ))}
        <label data-testid={testid} className="inline-flex h-14 min-w-[3.5rem] cursor-pointer items-center justify-center rounded-lg border-2 border-dashed border-violet-300 bg-violet-50 text-violet-600 hover:border-violet-500 active:scale-95">
          {uploading ? <Upload className="h-5 w-5 animate-pulse" /> : <Camera className="h-5 w-5" />}
          <input type="file" accept="image/*" capture="environment" multiple hidden onChange={onChange} />
        </label>
      </div>
    </div>
  );
}

// ─── QUICK DAILY LOG ────────────────────────────────────────────
function QuickDailyLog({ projectId, areas, gps, onClose, onSaved }) {
  const today = new Date().toISOString().slice(0, 10);
  const [areaId, setAreaId] = useState(() => getRecentAreas(projectId)[0] || "");
  const [labor, setLabor] = useState(0);
  const [equipment, setEquipment] = useState(0);
  const [engineer, setEngineer] = useState(() => localStorage.getItem(STORAGE_ENGINEER(projectId)) || "");
  const [notes, setNotes] = useState("");
  const [photos, setPhotos] = useState([]);
  const [saving, setSaving] = useState(false);
  const [hasYesterday, setHasYesterday] = useState(false);

  // Probe for "yesterday" data on area change
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const r = await axiosInstance.get(`/pec/daily-logs/last?project_id=${projectId}${areaId ? `&area_id=${areaId}` : ""}`);
        if (!alive) return;
        setHasYesterday(!!r.data?.id);
      } catch { /* ignore */ }
    })();
    return () => { alive = false; };
  }, [projectId, areaId]);

  const applyYesterday = async () => {
    try {
      const r = await axiosInstance.get(`/pec/daily-logs/last?project_id=${projectId}${areaId ? `&area_id=${areaId}` : ""}`);
      const d = r.data;
      if (!d?.id) { toast.info("لا يوجد سجل سابق لهذه المنطقة"); return; }
      setLabor(d.labor_actual || 0);
      setEquipment(d.equipment_active || 0);
      if (d.engineer_name) setEngineer(d.engineer_name);
      toast.success("تم نسخ بيانات أمس");
    } catch { toast.error("تعذّر جلب بيانات أمس"); }
  };

  const save = async () => {
    setSaving(true);
    try {
      await axiosInstance.post("/pec/daily-logs", {
        project_id: projectId,
        area_id: areaId || null,
        log_date: today,
        labor_actual: Number(labor),
        labor_planned: Number(labor),       // speed mode: same as actual
        equipment_active: Number(equipment),
        engineer_name: engineer,
        notes,
        photo_urls: photos,
        gps: gps || undefined,
      });
      rememberArea(projectId, areaId);
      if (engineer) localStorage.setItem(STORAGE_ENGINEER(projectId), engineer);
      toast.success("تم حفظ السجل اليومي ✓");
      onSaved?.();
      onClose();
    } catch { toast.error("فشل الحفظ"); }
    finally { setSaving(false); }
  };

  return (
    <div data-testid="qa-daily" className="space-y-4">
      <div>
        <div className="mb-1 text-[11px] font-semibold text-slate-600">المنطقة</div>
        <AreaChips areas={areas} projectId={projectId} value={areaId} onChange={setAreaId} />
      </div>

      <div className="grid grid-cols-2 gap-3">
        <CounterPill testid="qa-daily-labor"     label="عدد العمال"   value={labor}     onChange={setLabor} />
        <CounterPill testid="qa-daily-equipment" label="عدد المعدات"  value={equipment} onChange={setEquipment} />
      </div>

      {hasYesterday && (
        <button
          data-testid="qa-daily-yesterday"
          onClick={applyYesterday}
          className="inline-flex w-full items-center justify-center gap-2 rounded-xl border border-violet-300 bg-violet-50 px-4 py-3 text-sm font-bold text-violet-700 hover:bg-violet-100 active:scale-[0.99]"
        >
          <History className="h-4 w-4" /> مثل أمس (نفس المنطقة)
        </button>
      )}

      <div>
        <div className="mb-1 text-[11px] font-semibold text-slate-600">المهندس</div>
        <input
          data-testid="qa-daily-engineer"
          value={engineer}
          onChange={(e) => setEngineer(e.target.value)}
          placeholder="اسم المهندس"
          className="w-full rounded-xl border border-slate-200 px-3 py-3 text-base"
        />
      </div>

      <div>
        <div className="mb-1 text-[11px] font-semibold text-slate-600">ملاحظات (اختياري)</div>
        <textarea
          data-testid="qa-daily-notes"
          rows={2}
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm"
        />
      </div>

      <MultiCamera testid="qa-daily-cam" projectId={projectId} kind="daily_photo" gps={gps}
                   urls={photos} onAdd={(arr) => setPhotos(p => [...p, ...arr])} />

      <button
        data-testid="qa-daily-save"
        onClick={save}
        disabled={saving}
        className="sticky bottom-0 mt-3 w-full rounded-xl bg-gradient-to-r from-violet-600 to-violet-700 py-4 text-base font-bold text-white shadow-lg shadow-violet-500/30 active:scale-[0.98] disabled:opacity-60"
      >
        {saving ? "جارٍ الحفظ…" : `حفظ السجل · ${today}`}
      </button>
    </div>
  );
}

// ─── QUICK MAR ──────────────────────────────────────────────────
function QuickMAR({ projectId, areas, gps, onClose, onSaved }) {
  const today = new Date().toISOString().slice(0, 10);
  const [areaId, setAreaId] = useState(() => getRecentAreas(projectId)[0] || "");
  const [status, setStatus] = useState("Submitted");
  const [materials, setMaterials] = useState("");
  const [photos, setPhotos] = useState([]);
  const [fileUrl, setFileUrl] = useState("");
  const [saving, setSaving] = useState(false);

  const onDoc = async (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    try {
      const r = await quickUpload(projectId, "mar_doc", f, gps);
      setFileUrl(r.url);
      toast.success("تم رفع الملف");
    } catch { toast.error("فشل الرفع"); }
    e.target.value = "";
  };

  const save = async () => {
    setSaving(true);
    try {
      await axiosInstance.post("/pec/mar", {
        project_id: projectId,
        area_id: areaId || null,
        received_date: today,
        materials: materials ? materials.split("،").map(n => ({ name: n.trim() })).filter(m => m.name) : [],
        status,
        file_url: fileUrl || null,
        photo_urls: photos,
        gps: gps || undefined,
      });
      rememberArea(projectId, areaId);
      toast.success("تم حفظ MAR ✓");
      onSaved?.(); onClose();
    } catch { toast.error("فشل الحفظ"); }
    finally { setSaving(false); }
  };

  return (
    <div data-testid="qa-mar" className="space-y-4">
      <div>
        <div className="mb-1 text-[11px] font-semibold text-slate-600">المنطقة</div>
        <AreaChips areas={areas} projectId={projectId} value={areaId} onChange={setAreaId} />
      </div>
      <div>
        <div className="mb-1 text-[11px] font-semibold text-slate-600">المواد المستلمة</div>
        <input
          data-testid="qa-mar-materials"
          value={materials}
          onChange={(e) => setMaterials(e.target.value)}
          placeholder="حديد، أسمنت، رمل"
          className="w-full rounded-xl border border-slate-200 px-3 py-3 text-base"
        />
      </div>
      <div>
        <div className="mb-1 text-[11px] font-semibold text-slate-600">الحالة</div>
        <StatusChips testid="qa-mar-status" statuses={MAR_STATUSES} value={status} onChange={setStatus} />
      </div>
      <div className="grid grid-cols-2 gap-2">
        <label data-testid="qa-mar-doc" className="inline-flex h-12 cursor-pointer items-center justify-center gap-1 rounded-xl border border-slate-300 bg-white text-xs font-semibold text-slate-700 hover:border-violet-400 active:scale-95">
          <Upload className="h-4 w-4" />
          {fileUrl ? "تم رفع الملف ✓" : "رفع PDF/MAR"}
          <input type="file" hidden accept=".pdf,image/*" onChange={onDoc} />
        </label>
        <div></div>
      </div>
      <MultiCamera testid="qa-mar-cam" projectId={projectId} kind="mar_photo" gps={gps}
                   urls={photos} onAdd={(arr) => setPhotos(p => [...p, ...arr])} label="صور الاستلام" />
      <button
        data-testid="qa-mar-save"
        onClick={save}
        disabled={saving}
        className="sticky bottom-0 mt-3 w-full rounded-xl bg-gradient-to-r from-violet-600 to-violet-700 py-4 text-base font-bold text-white shadow-lg active:scale-[0.98] disabled:opacity-60"
      >
        {saving ? "جارٍ الحفظ…" : "حفظ MAR"}
      </button>
    </div>
  );
}

// ─── QUICK MIR ──────────────────────────────────────────────────
function QuickMIR({ projectId, areas, gps, onClose, onSaved }) {
  const today = new Date().toISOString().slice(0, 10);
  const [areaId, setAreaId] = useState(() => getRecentAreas(projectId)[0] || "");
  const [status, setStatus] = useState("Inspected");
  const [qty, setQty] = useState(0);
  const [consultant, setConsultant] = useState("");
  const [before_, setBefore] = useState([]);
  const [during, setDuring] = useState([]);
  const [after_, setAfter] = useState([]);
  const [fileUrl, setFileUrl] = useState("");
  const [saving, setSaving] = useState(false);

  const onDoc = async (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    try { const r = await quickUpload(projectId, "mir_doc", f, gps); setFileUrl(r.url); toast.success("تم رفع الملف"); }
    catch { toast.error("فشل الرفع"); }
    e.target.value = "";
  };

  const save = async () => {
    setSaving(true);
    try {
      await axiosInstance.post("/pec/mir", {
        project_id: projectId,
        area_id: areaId || null,
        inspection_date: today,
        quantity_executed: Number(qty),
        consultant_name: consultant,
        status,
        file_url: fileUrl || null,
        photo_urls: during,
        before_photo_urls: before_,
        after_photo_urls: after_,
        gps: gps || undefined,
      });
      rememberArea(projectId, areaId);
      toast.success("تم حفظ MIR ✓");
      onSaved?.(); onClose();
    } catch { toast.error("فشل الحفظ"); }
    finally { setSaving(false); }
  };

  return (
    <div data-testid="qa-mir" className="space-y-4">
      <div>
        <div className="mb-1 text-[11px] font-semibold text-slate-600">المنطقة</div>
        <AreaChips areas={areas} projectId={projectId} value={areaId} onChange={setAreaId} />
      </div>
      <div className="grid grid-cols-2 gap-3">
        <CounterPill testid="qa-mir-qty" label="الكمية المنفذة" value={qty} onChange={setQty} />
        <div className="rounded-xl border border-slate-200 bg-white p-3">
          <div className="mb-1 text-[11px] font-semibold text-slate-600">الاستشاري</div>
          <input
            data-testid="qa-mir-consultant"
            value={consultant}
            onChange={(e) => setConsultant(e.target.value)}
            className="mt-1 w-full rounded-lg border border-slate-200 px-2 py-2 text-sm"
          />
        </div>
      </div>
      <div>
        <div className="mb-1 text-[11px] font-semibold text-slate-600">الحالة</div>
        <StatusChips testid="qa-mir-status" statuses={MIR_STATUSES} value={status} onChange={setStatus} />
      </div>
      <label data-testid="qa-mir-doc" className="inline-flex h-12 cursor-pointer items-center justify-center gap-1 rounded-xl border border-slate-300 bg-white text-xs font-semibold text-slate-700 hover:border-violet-400 active:scale-95">
        <Upload className="h-4 w-4" />
        {fileUrl ? "تم رفع الملف ✓" : "رفع MIR PDF"}
        <input type="file" hidden accept=".pdf,image/*" onChange={onDoc} />
      </label>
      <div className="grid grid-cols-3 gap-2">
        <MultiCamera testid="qa-mir-before"  projectId={projectId} kind="mir_before" gps={gps} urls={before_} onAdd={(arr) => setBefore(p => [...p, ...arr])} label="قبل" />
        <MultiCamera testid="qa-mir-during"  projectId={projectId} kind="mir_during" gps={gps} urls={during}  onAdd={(arr) => setDuring(p => [...p, ...arr])} label="أثناء" />
        <MultiCamera testid="qa-mir-after"   projectId={projectId} kind="mir_after"  gps={gps} urls={after_}  onAdd={(arr) => setAfter(p => [...p, ...arr])}  label="بعد" />
      </div>
      <button
        data-testid="qa-mir-save"
        onClick={save}
        disabled={saving}
        className="sticky bottom-0 mt-3 w-full rounded-xl bg-gradient-to-r from-violet-600 to-violet-700 py-4 text-base font-bold text-white shadow-lg active:scale-[0.98] disabled:opacity-60"
      >
        {saving ? "جارٍ الحفظ…" : "حفظ MIR"}
      </button>
    </div>
  );
}

// ─── QUICK PHOTO (one-tap evidence with GPS) ───────────────────
function QuickPhoto({ projectId, gps, onClose, onSaved }) {
  const [photos, setPhotos] = useState([]);
  return (
    <div data-testid="qa-photo" className="space-y-4">
      <p className="text-sm text-slate-600">
        التقط صور فورية للموقع — GPS {gps ? "مفعّل ✓" : "غير متاح"} وستُرفق تلقائياً بالـ Evidence Vault.
      </p>
      <MultiCamera testid="qa-photo-cam" projectId={projectId} kind="quick_photo" gps={gps}
                   urls={photos} onAdd={(arr) => setPhotos(p => [...p, ...arr])} label="صور فورية" />
      <button
        data-testid="qa-photo-done"
        onClick={() => { onSaved?.(); onClose(); }}
        className="w-full rounded-xl bg-emerald-600 py-4 text-base font-bold text-white active:scale-[0.98]"
      >
        تم ({photos.length} صورة)
      </button>
    </div>
  );
}

// ─── MAIN: FAB + sheet router ────────────────────────────────
export default function QuickActions({ projectId, areas, onChanged }) {
  const [open, setOpen] = useState(false);
  const [sheet, setSheet] = useState(null);    // 'daily' | 'mar' | 'mir' | 'photo'
  const gps = useGps();

  const triggerSheet = (key) => { setSheet(key); setOpen(false); };
  const closeSheet = () => setSheet(null);
  const handleSaved = useCallback(() => { onChanged?.(); }, [onChanged]);

  if (!projectId) return null;

  return (
    <>
      {/* FAB */}
      <div className="fixed bottom-5 left-5 z-50 flex flex-col items-end gap-2 md:bottom-7 md:left-7" dir="ltr">
        {open && (
          <div data-testid="qa-fab-menu" className="mb-1 flex flex-col items-end gap-2 animate-in fade-in slide-in-from-bottom-2">
            <FabAction testid="qa-fab-daily"  icon={Calendar}        label="سجل اليوم" onClick={() => triggerSheet("daily")} />
            <FabAction testid="qa-fab-mar"    icon={Boxes}           label="MAR"       onClick={() => triggerSheet("mar")} />
            <FabAction testid="qa-fab-mir"    icon={ClipboardCheck}  label="MIR"       onClick={() => triggerSheet("mir")} />
            <FabAction testid="qa-fab-photo"  icon={Camera}          label="صورة فورية" onClick={() => triggerSheet("photo")} />
          </div>
        )}
        <button
          data-testid="qa-fab"
          onClick={() => setOpen(o => !o)}
          aria-label="إجراءات سريعة"
          className={`inline-flex h-14 w-14 items-center justify-center rounded-full bg-gradient-to-br from-violet-600 to-violet-800 text-white shadow-2xl shadow-violet-500/40 transition-transform active:scale-90 ${open ? "rotate-45" : ""}`}
        >
          <Plus className="h-6 w-6" />
        </button>
        {gps && (
          <div className="mt-1 inline-flex items-center gap-1 rounded-full bg-emerald-100 px-2 py-0.5 text-[10px] font-semibold text-emerald-700">
            <MapPin className="h-3 w-3" /> GPS
          </div>
        )}
      </div>

      <Sheet open={sheet === "daily"} onClose={closeSheet} title="سجل اليوم — Speed Mode">
        <QuickDailyLog projectId={projectId} areas={areas} gps={gps} onClose={closeSheet} onSaved={handleSaved} />
      </Sheet>
      <Sheet open={sheet === "mar"} onClose={closeSheet} title="MAR — Speed Mode">
        <QuickMAR projectId={projectId} areas={areas} gps={gps} onClose={closeSheet} onSaved={handleSaved} />
      </Sheet>
      <Sheet open={sheet === "mir"} onClose={closeSheet} title="MIR — Speed Mode">
        <QuickMIR projectId={projectId} areas={areas} gps={gps} onClose={closeSheet} onSaved={handleSaved} />
      </Sheet>
      <Sheet open={sheet === "photo"} onClose={closeSheet} title="صورة فورية — Evidence Vault">
        <QuickPhoto projectId={projectId} gps={gps} onClose={closeSheet} onSaved={handleSaved} />
      </Sheet>
    </>
  );
}

function FabAction({ icon: Icon, label, onClick, testid }) {
  return (
    <button
      data-testid={testid}
      onClick={onClick}
      className="inline-flex items-center gap-2 rounded-full bg-white px-3 py-2 text-sm font-semibold text-slate-800 shadow-lg ring-1 ring-slate-200 hover:bg-slate-50 active:scale-95"
      dir="rtl"
    >
      <span className="inline-flex h-8 w-8 items-center justify-center rounded-full bg-violet-100 text-violet-700">
        <Icon className="h-4 w-4" />
      </span>
      {label}
    </button>
  );
}
